/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { Box, FormControl, Switch, useTheme } from 'native-base';

import {
    useUpdateMember,
    useAppSettings,
    useI18n,
    useSpringBigStore
} from '@springbig/shared';

import Alert from '~/shared/Alert';
import Section from '~/shared/Section';
import { Caption } from '~/shared/Typography';
import { useAccountSettingsStyles, useGeneralStyles } from '~/shared/styles';
import Button from '~/shared/Button';
import { useAuthorization } from '~/hooks/auth';
import Loading from '~/shared/Loading';

export type SettingFormData = {
    allowed_sms: boolean;
    allowed_email: boolean;
    allowed_loyalty: boolean;
    allow_push_notifications: boolean;
};

const SettingsScreen = () => {

    const [isDisabled, setIsDisabled] = useState(true);
    const { t } = useI18n();
    const { member } = useSpringBigStore();
    const { mutate: updateMember } = useUpdateMember();
    const [formData, setFormData] = React.useState<SettingFormData>({
        allowed_sms: false,
        allowed_email: false,
        allowed_loyalty: false,
        allow_push_notifications: false
    });
    const [showAlert, setShowAlert] = useState<{
        status: boolean,
        id: 'notification' | 'closeNotification' | null,
        title: string,
        text: string,
        type?: string,
        disclaimer?: string,
        disable_signature?: boolean,
        opt_state?: 'opt_in' | 'opt_out';
    }>({ status: false, id: null, title: '', text: '', type: '', opt_state: undefined });
    const [signature, setSignature] = useState<string>();
    const { merchantOrg, account, loyalty_wallet_opt_in } = useAppSettings();
    const {
        text
    } = useAccountSettingsStyles();
    const gStyle = useGeneralStyles();
    const theme = useTheme();
    const { isLoadingAuth } = useAuthorization();

    useEffect(() => {

        if (member) {
            setFormData({
                allowed_sms: !!member.allowed_sms,
                allowed_email: !!member.allowed_email,
                allowed_loyalty: !!member.allowed_loyalty,
                allow_push_notifications: !!member.allow_push_notifications
            });
        }

    }, [member]);

    const handleChanges = (value: boolean, type: 'sms' | 'push_notification' | 'email' | 'loyalty') => {

        let title;
        let text;
        let messageType;
        let disclaimer;
        let disable_signature;
        let opt_state;

        switch (type) {
            case 'sms':
                messageType = 'messaging';
                disclaimer = t('settings.sms_opt_in_disclaimer', [merchantOrg]);
                title = value ? 'Opt out of text marketing' : 'Opt in to text marketing';
                opt_state = value ? 'opt_out' : 'opt_in';
                text = 'Are you sure you want to opt out of receiving sms notifications?';
                disable_signature = !!account?.disable_signature?.sms;
                break;
            case 'push_notification':
                messageType = 'messaging';
                disclaimer = '';
                title = value ? 'Opt out of push notification' : 'Opt in to push notification';
                opt_state = value ? 'opt_out' : 'opt_in';
                text = 'Are you sure you want to opt out of receiving push notifications?';
                break;
            case 'email':
                messageType = 'messaging';
                disclaimer = t('settings.email_opt_in_disclaimer', [merchantOrg]);
                title = value ? 'Opt out of email marketing' : 'Opt in to email marketing';
                opt_state = value ? 'opt_out' : 'opt_in';
                text = 'Are you sure you want to opt out of receiving email notifications?';
                disable_signature = !!account?.disable_signature?.email;
                break;
            case 'loyalty':
                messageType = 'loyalty program';
                disclaimer = '';
                title = value ? 'Opt out of loyalty program' : 'Opt in to loyalty program';
                opt_state = value ? 'opt_out' : 'opt_in';
                text = 'Are you sure you want to opt out of the loyalty program?';
                disable_signature = !!account?.disable_signature?.loyalty;
        }

        if (!value) {
            setShowAlert({ status: true, id: 'notification', title, text, type: messageType, disclaimer, disable_signature, opt_state });
        }
        else {
            setShowAlert({ status: true, id: 'closeNotification', title, text, type: messageType, disclaimer, disable_signature, opt_state });
        }
    };

    const submitFormData = () => {

        updateMember({
            member: {
                allowed_sms: formData.allowed_sms,
                allowed_email: formData.allowed_email,
                allowed_loyalty: formData.allowed_loyalty,
                // allow_push_notifications: formData.allow_push_notifications
            },
            signature
        });

    };

    const handleCancelConditions = () => {
        if (!member) {
            return;
        }

        setShowAlert({ status: false, id: null, title: '', text: '', type: '' });
        setFormData({
            allowed_sms: !!member.allowed_sms,
            allowed_email: !!member.allowed_email,
            allowed_loyalty: !!member.allowed_loyalty,
            allow_push_notifications: !!member.allow_push_notifications
        });
    };

    const handleAcceptConditions = () => {

        setShowAlert({ status: false, id: null, title: '', text: '', type: '' });
    };

    if (isLoadingAuth) {
        return <Loading isLoading={isLoadingAuth} />;
    }

    type Form = {
        enabled: boolean;
        type: 'sms' | 'push_notification' | 'email' | 'loyalty';
        title: string;
        subtitle: string;
        variable: string;
    }[];

    const form: Form = [
        {
            enabled: true,
            type: 'sms',
            title: 'Text Marketing Communications',
            subtitle: `Allow ${member?.merchant_name} to send me marketing communication via text`,
            variable: 'allowed_sms',
        },
        {
            enabled: !account?.notification?.hideEmailToggle,
            type: 'email',
            title: 'Email Marketing Communications',
            subtitle: `Allow ${member?.merchant_name} to send me marketing communication via email`,
            variable: 'allowed_email',
        },
        {
            enabled: !!loyalty_wallet_opt_in && !!member?.manual_loyalty,
            type: 'loyalty',
            title: 'Loyalty Program',
            subtitle: `Allow app to track my points for loyalty purposes`,
            variable: 'allowed_loyalty',
        },
    ];

    return (
        <Section flex={1}>
            <Box
                minH={'100%'}
            >
                <Box
                    flexDirection={'row'}
                    justifyContent='space-between'
                    alignItems={'center'}
                    p={4}
                >
                    <Caption
                        color={text}
                        text='settings.header_title'
                    />
                    {
                        isDisabled ? <Button
                            bg={gStyle.button_background}
                            onPress={() => setIsDisabled(false)}
                        >
                            <Caption
                                text='general.edit'
                                color={gStyle.button_text}
                            />
                        </Button> : <Button
                            bg={gStyle.button_background}
                            onPress={() => {

                                setIsDisabled(true);
                                submitFormData();
                            }}>
                            <Caption
                                text='general.save'
                                color={gStyle.button_text}
                            />
                        </Button>
                    }
                </Box>
                <Box>
                    <FormControl isDisabled={isDisabled} mb={4}>
                        {form.map((checkbox, index) => {
                            if (checkbox.enabled) {
                                return (
                                    <Box
                                        key={index}
                                        bg={gStyle.section_background}
                                        px={4}
                                        py={4}
                                        mb={4}
                                        alignItems='center'
                                        flexDirection={'row'}
                                        justifyContent='space-between'
                                    >
                                        <Box flex={1}>
                                            <Caption
                                                color={text}
                                            >{checkbox.title}</Caption>
                                            <Caption opacity={.5}>{checkbox.subtitle}</Caption>
                                        </Box>
                                        <Switch
                                            onTrackColor={theme.colors.secondary}
                                            onThumbColor={theme.colors.primary}
                                            offThumbColor={theme.colors.grayDark}
                                            offTrackColor={theme.colors.grayLight}
                                            _hover={
                                                {
                                                    onTrackColor: theme.colors.secondary,
                                                    onThumbColor: theme.colors.primary,
                                                    offThumbColor: theme.colors.grayDark,
                                                    offTrackColor: theme.colors.grayLight,
                                                }
                                            }
                                            isChecked={formData[checkbox.variable]}
                                            onValueChange={() => {

                                                handleChanges(!!formData[checkbox.variable], checkbox.type);
                                                setFormData((prevState) => ({ ...prevState, [checkbox.variable]: !prevState[checkbox.variable] }));
                                            }}
                                        />
                                    </Box>
                                );
                            }
                        }
                        )}
                    </FormControl>
                </Box>
            </Box>
            {
                showAlert.status && showAlert.id === 'notification' && (
                    <Alert
                        type={showAlert.opt_state}
                        checkMessage={true}
                        signature={!showAlert.disable_signature}
                        setSignature={(sign) => setSignature(sign)}
                        title={showAlert.title || ''}
                        subHeaderText={t('settings.modal_paragraph', [
                            showAlert.disable_signature ? '' : 'and sign ',
                            showAlert.type
                        ])}
                        message={showAlert.disclaimer || ''}
                        setAcceptAlert={handleAcceptConditions}
                        setCancelAlert={handleCancelConditions}
                        showAlert={showAlert.status}
                        textConfirmButton='Opt in'
                    />
                )
            }
            {
                showAlert.status && showAlert.id === 'closeNotification' && (
                    <Alert
                        title={showAlert.title}
                        message={showAlert.text}
                        setAcceptAlert={handleAcceptConditions}
                        setCancelAlert={handleCancelConditions}
                        showAlert={showAlert.status}
                    />
                )
            }
        </Section>
    );
};

export default SettingsScreen;
